import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Star, Clock, Users, Award, Calendar, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const ClassDetailPage = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    fetchClassDetails();
  }, [id]);

  const fetchClassDetails = async () => {
    try {
      const response = await axios.get(`${API}/classes/${id}`);
      setClassData(response.data);
    } catch (error) {
      toast.error('Failed to load class details');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!user) {
      toast.error('Please login to enroll');
      navigate('/auth');
      return;
    }

    if (user.role !== 'student') {
      toast.error('Only students can enroll in classes');
      return;
    }

    setEnrolling(true);
    try {
      await axios.post(`${API}/enrollments?class_id=${id}`);
      toast.success('Successfully enrolled in class!');
      navigate('/dashboard/student');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Enrollment failed');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!classData) {
    return <div className="min-h-screen flex items-center justify-center">Class not found</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="class-detail-page">
      <SEO
        title={classData.title}
        description={`${classData.title} - ${classData.description?.substring(0, 150)}. Enroll now at Sun Kamal Academy.`}
        path={`/classes/${id}`}
      />
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_sunkamalplay/artifacts/oha9raa2_logo-kamal.webp" alt="Sun Kamal Academy" className="h-10 sm:h-12" />
          </Link>
          <div className="flex gap-2 sm:gap-3">
            <Link to="/classes"><Button variant="ghost" className="text-sm">Back to Classes</Button></Link>
            {user && <Button onClick={() => navigate(`/dashboard/${user.role}`)} className="text-sm">Dashboard</Button>}
          </div>
        </div>
      </header>

      {/* Class Hero */}
      <div className="bg-gradient-to-r from-orange-500 to-yellow-500">
        <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 items-center">
            <div className="text-white">
              <div className="flex gap-2 mb-3 sm:mb-4 flex-wrap">
                <span className="px-3 py-1 bg-white/20 backdrop-blur rounded-full text-xs sm:text-sm font-medium">
                  {classData.sport}
                </span>
                <span className="px-3 py-1 bg-white/20 backdrop-blur rounded-full text-xs sm:text-sm font-medium">
                  {classData.skill_level}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-3 sm:mb-4" data-testid="class-title">{classData.title}</h1>
              <p className="text-base sm:text-lg md:text-xl mb-4 sm:mb-6 opacity-90">{classData.description}</p>
              <div className="flex items-center gap-4 sm:gap-6 flex-wrap text-sm sm:text-base">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                  {classData.duration} minutes
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                  {classData.enrolled_count}/{classData.max_students} enrolled
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-white" />
                  {classData.rating.toFixed(1)}
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 shadow-xl">
              <div className="text-3xl sm:text-4xl font-bold text-orange-600 mb-4 sm:mb-6">${classData.price}</div>
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span>Live instructor-led sessions</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span>Certificate of completion</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span>Lifetime access to materials</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span>Progress tracking</span>
                </div>
              </div>
              <Button
                onClick={handleEnroll}
                disabled={enrolling || classData.enrolled_count >= classData.max_students}
                className="w-full btn-primary text-lg py-6"
                data-testid="enroll-btn"
              >
                {enrolling ? 'Enrolling...' : classData.enrolled_count >= classData.max_students ? 'Class Full' : 'Enroll Now'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Class Details */}
      <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          <div className="md:col-span-2 space-y-6 sm:space-y-8">
            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">About This Class</h2>
              <p className="text-gray-600 leading-relaxed">{classData.description}</p>
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">What You'll Learn</h2>
              <ul className="space-y-3">
                {[
                  'Fundamentals and advanced techniques',
                  'Proper form and safety practices',
                  'Game strategies and tactics',
                  'Physical conditioning and training'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">Schedule</h2>
              <div className="flex items-center gap-3 text-gray-600">
                <Calendar className="w-5 h-5" />
                <span>{classData.schedule}</span>
              </div>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-xl font-bold mb-4">Instructor</h3>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                  {classData.teacher_name.charAt(0)}
                </div>
                <div>
                  <div className="font-bold">{classData.teacher_name}</div>
                  <div className="text-sm text-gray-600">Certified Instructor</div>
                </div>
              </div>
              <p className="text-gray-600 text-sm">Expert {classData.sport} coach with years of experience</p>
            </Card>

            <Card className="p-6">
              <h3 className="text-xl font-bold mb-4">Class Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration</span>
                  <span className="font-medium">{classData.duration} min</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Level</span>
                  <span className="font-medium capitalize">{classData.skill_level}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Max Students</span>
                  <span className="font-medium">{classData.max_students}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Enrolled</span>
                  <span className="font-medium">{classData.enrolled_count}</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassDetailPage;